import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Settings, Plus, Eye, EyeOff, Copy, Check, BarChart3, Users, Calendar, TrendingUp, Lock, Unlock, Download, Upload, Edit, Trash2, Save, X, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { testCodeStorage, statsStorage, globalSettings, localSettings } from '../services/persistentStorage';
import { blogService, BlogPost } from '../services/blogService';
import TeamAdmin from './TeamAdmin';

interface TestCode {
  id: string;
  code: string;
  isUsed: boolean;
  usedAt?: string;
  createdAt: string;
  status?: 'available' | 'distributed' | 'used';
  source?: 'manual' | 'auto'; // 区分手动生成和自动生成
  assessmentType?: 'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test'; // 新增：测评类型
}

interface AdminStats {
  totalTests: number;
  totalVisits: number;
  totalCompletions: number;
  testCodes: TestCode[];
  referralEnabled?: boolean;
  // 按测评类型分类的统计
  testsByType: {
    'emotional-memory-pattern': number;
    'big-five-personality': number;
    '34-talent-test': number;
  };
  completionsByType: {
    'emotional-memory-pattern': number;
    'big-five-personality': number;
    '34-talent-test': number;
  };
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // 从localStorage恢复登录状态
    return localStorage.getItem('adminAuthenticated') === 'true';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [newCodeCount, setNewCodeCount] = useState(1);
  const [copiedCode, setCopiedCode] = useState('');
  const [activeTab, setActiveTab] = useState<'codes' | 'stats' | 'settings' | 'blog' | 'team'>('codes');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test'>('emotional-memory-pattern');
  const [selectedSettingsAssessmentType, setSelectedSettingsAssessmentType] = useState<'emotional-memory-pattern' | 'big-five-personality' | '34-talent-test'>('emotional-memory-pattern');
  const [hideUsedCodes, setHideUsedCodes] = useState<boolean>(() => {
    return localStorage.getItem('admin_hide_used_codes') === 'true';
  });
  const [testModeOpen, setTestModeOpen] = useState<{
    'emotional-memory-pattern': boolean;
    'big-five-personality': boolean;
    '34-talent-test': boolean;
  }>({
    'emotional-memory-pattern': false,
    'big-five-personality': false,
    '34-talent-test': false
  });

  // 博客管理相关状态
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [blogError, setBlogError] = useState('');
  const [blogSuccess, setBlogSuccess] = useState('');

  // 初始化测评模式设置
  useEffect(() => {
    const initializeTestModeSettings = async () => {
      try {
        const emotionalMode = await localSettings.getTestModeOpen('emotional-memory-pattern');
        const bigFiveMode = await localSettings.getTestModeOpen('big-five-personality');
        const talentTestMode = await localSettings.getTestModeOpen('34-talent-test');
        
        setTestModeOpen({
          'emotional-memory-pattern': emotionalMode,
          'big-five-personality': bigFiveMode,
          '34-talent-test': talentTestMode
        });
      } catch (error) {
        console.error('初始化测评模式设置失败:', error);
        // 使用默认值
        setTestModeOpen({
          'emotional-memory-pattern': false,
          'big-five-personality': false,
          '34-talent-test': false
        });
      }
    };
    
    initializeTestModeSettings();
  }, []);

  // 如果已登录，自动加载数据
  useEffect(() => {
    if (isAuthenticated) {
      loadAdminData();
    }
  }, [isAuthenticated]);

  // 监听标签页切换，刷新数据
  useEffect(() => {
    if (isAuthenticated) {
      loadAdminData();
    }
  }, [activeTab, isAuthenticated]);

  // 简单的密码验证
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'admin123') {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuthenticated', 'true');
      setAuthError('');
      setPassword(''); // 清空密码
    } else {
      setAuthError('密码错误');
    }
  };

  // 登出功能
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuthenticated');
    setStats(null);
    navigate('/');
  };

  // 加载管理数据
  const loadAdminData = async () => {
    if (isLoading) return; // 防止重复加载
    
    setIsLoading(true);
    try {
      // 加载测试码数据
      const adminCodes = await testCodeStorage.getTestCodes();
      
      // 加载访问统计
      const statsData = await statsStorage.getStats();
      
      // 加载推荐功能状态
      const referralEnabled = await globalSettings.getReferralEnabled();
      
      // 加载博客数据
      const allBlogPosts = await blogService.getAllPosts();
      setBlogPosts(allBlogPosts);
      
      setStats({
        totalTests: statsData.totalTests,
        totalVisits: statsData.totalVisits,
        totalCompletions: statsData.totalCompletions,
        referralEnabled: referralEnabled,
        testCodes: adminCodes,
        testsByType: statsData.testsByType,
        completionsByType: statsData.completionsByType
      });
    } catch (error) {
      console.error('加载管理员数据失败:', error);
      setAuthError('数据加载失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 生成测试码
  const generateTestCodes = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setAuthError(''); // 清除之前的错误
    try {
      const newCodes = [];
      const existingCodes = await testCodeStorage.getTestCodes();
      
      for (let i = 0; i < newCodeCount; i++) {
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        newCodes.push({
          id: `${Date.now()}-${i}`,
          code,
          isUsed: false,
          status: 'available' as const,
          createdAt: new Date().toISOString(),
          source: 'manual' as const,
          assessmentType: selectedAssessmentType
        });
      }
      
      console.log('准备保存测试码:', newCodes);
      const updatedCodes = [...existingCodes, ...newCodes];
      const saveResult = await testCodeStorage.saveTestCodes(updatedCodes);
      
      if (!saveResult) {
        throw new Error('保存测试码失败，请检查网络连接或联系管理员');
      }
      
      console.log('测试码保存成功');
      setNewCodeCount(1);
      // 重新加载数据
      await loadAdminData();
      setAuthError(''); // 成功后清除错误信息
    } catch (error) {
      console.error('生成测试码失败:', error);
      const errorMessage = error instanceof Error ? error.message : '生成测试码失败，请重试';
      setAuthError(`生成失败: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 更新测试码状态
  const updateCodeStatus = async (codeId: string, newStatus: 'available' | 'distributed' | 'used') => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      await testCodeStorage.updateTestCode(codeId, { status: newStatus });
      // 重新加载数据
      await loadAdminData();
    } catch (error) {
      console.error('更新测试码状态失败:', error);
      setAuthError('更新状态失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 复制测试码
  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 删除已使用的测试码
  const deleteUsedCodes = async () => {
    if (isLoading) return;
    
    const confirmDelete = window.confirm('确定要删除所有已使用的测试码吗？此操作不可撤销。');
    if (!confirmDelete) return;
    
    setIsLoading(true);
    try {
      const codes = await testCodeStorage.getTestCodes();
      const activeCodes = codes.filter(code => code.status !== 'used');
      
      const saveResult = await testCodeStorage.saveTestCodes(activeCodes);
      if (!saveResult) {
        throw new Error('删除失败，请重试');
      }
      
      await loadAdminData();
      setAuthError('');
    } catch (error) {
      console.error('删除已使用测试码失败:', error);
      const errorMessage = error instanceof Error ? error.message : '删除失败，请重试';
      setAuthError(`删除失败: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 过滤测试码
  const filteredTestCodes = useMemo(() => {
    if (!stats?.testCodes) return [];
    
    if (hideUsedCodes) {
      return stats.testCodes.filter(code => code.status !== 'used');
    }
    
    return stats.testCodes;
  }, [stats?.testCodes, hideUsedCodes]);

  // 博客管理函数
  const createNewPost = () => {
    const newPost: BlogPost = {
      id: Date.now().toString(),
      title: '',
      content: '',
      excerpt: '',
      author: '开发者',
      publishedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'draft',
      tags: [],
      readTime: 0,
      views: 0
    };
    setEditingPost(newPost);
    setIsCreatingPost(true);
    setBlogError('');
    setBlogSuccess('');
  };

  const savePost = async (post: BlogPost) => {
    try {
      // 计算阅读时间和摘要
      post.readTime = blogService.calculateReadTime(post.content);
      if (!post.excerpt) {
        post.excerpt = blogService.generateExcerpt(post.content);
      }
      
      const success = await blogService.savePost(post);
      if (success) {
        setBlogSuccess(isCreatingPost ? '文章创建成功！' : '文章更新成功！');
        setEditingPost(null);
        setIsCreatingPost(false);
        await loadAdminData();
        setTimeout(() => setBlogSuccess(''), 3000);
      } else {
        setBlogError('保存失败，请重试');
      }
    } catch (error) {
      console.error('保存文章失败:', error);
      setBlogError('保存失败，请重试');
    }
  };

  const deletePost = async (id: string) => {
    if (!window.confirm('确定要删除这篇文章吗？此操作不可撤销。')) return;
    
    try {
      const success = await blogService.deletePost(id);
      if (success) {
        setBlogSuccess('文章删除成功！');
        await loadAdminData();
        setTimeout(() => setBlogSuccess(''), 3000);
      } else {
        setBlogError('删除失败，请重试');
      }
    } catch (error) {
      console.error('删除文章失败:', error);
      setBlogError('删除失败，请重试');
    }
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setIsCreatingPost(false);
    setBlogError('');
    setBlogSuccess('');
  };

  // 登录界面
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#3A7D78] py-8 px-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 rounded-2xl p-8 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1)] w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Settings className="h-12 w-12 text-[#F5B6C1] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">管理员登录</h1>
            <p className="text-white/80">请输入管理员密码</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                className="w-full px-4 py-3 pr-12 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {authError && (
              <p className="text-red-300 text-sm">{authError}</p>
            )}

            <button
              type="submit"
              className="w-full bg-[#F5B6C1] text-[#3A7D78] py-3 px-6 rounded-lg font-medium hover:bg-[#F5B6C1]/90 transition-colors"
            >
              登录
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-white/60 hover:text-white text-sm transition-colors"
            >
              返回首页
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3A7D78] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* 头部 */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Settings className="h-8 w-8 text-[#F5B6C1]" />
            <h1 className="text-3xl font-bold text-white">管理后台</h1>
            {isLoading && (
              <div className="flex items-center gap-2 text-white/60">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                <span className="text-sm">加载中...</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAdminData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              <BarChart3 className="h-4 w-4" />
              刷新数据
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors"
            >
              登出
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>

        {/* 标签页 */}
        <div className="flex space-x-1 mb-8">
          {[
          { key: 'codes', label: '测试码管理', icon: Settings },
          { key: 'stats', label: '使用统计', icon: BarChart3 },
          { key: 'blog', label: '博客管理', icon: FileText },
          { key: 'team', label: '团队测评', icon: Users },
          { key: 'settings', label: '功能设置', icon: Settings }
        ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === key
                  ? 'bg-[#F5B6C1] text-[#3A7D78]'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
        </div>

        {/* 内容区域 */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 rounded-2xl p-8 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        >
          {activeTab === 'codes' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">测试码管理</h2>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={newCodeCount}
                    onChange={(e) => setNewCodeCount(parseInt(e.target.value) || 1)}
                    className="w-20 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#F5B6C1]"
                  />
                  <select
                    value={selectedAssessmentType}
                    onChange={(e) => setSelectedAssessmentType(e.target.value as 'emotional-memory-pattern' | 'big-five-personality')}
                    className="ml-3 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#F5B6C1]"
                  >
                    <option value="emotional-memory-pattern">情绪记忆测评</option>
      
                    <option value="big-five-personality">大五人格测评</option>
                  </select>
                  <button
                    onClick={generateTestCodes}
                    disabled={isLoading}
                    className="flex items-center gap-2 bg-[#F5B6C1] text-[#3A7D78] px-4 py-2 rounded-lg font-medium hover:bg-[#F5B6C1]/90 transition-colors disabled:opacity-50"
                  >
                    <Plus className="h-5 w-5" />
                    {isLoading ? '生成中...' : '生成测试码'}
                  </button>
                </div>
              </div>
              
              {/* 管理选项 */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hideUsedCodes}
                      onChange={(e) => {
                        setHideUsedCodes(e.target.checked);
                        localStorage.setItem('admin_hide_used_codes', e.target.checked.toString());
                      }}
                      className="w-4 h-4 text-[#F5B6C1] bg-gray-800 border-gray-600 rounded focus:ring-[#F5B6C1] focus:ring-2"
                    />
                    <span className="text-white/80 text-sm">隐藏已使用的测试码</span>
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-white/60 text-sm">
                    显示 {filteredTestCodes.length} / {stats?.testCodes.length || 0} 个测试码
                  </span>
                  
                  {stats?.testCodes.some(code => code.status === 'used') && (
                    <button
                      onClick={deleteUsedCodes}
                      disabled={isLoading}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-300 rounded text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50"
                    >
                      <span>🗑️</span>
                      清理已使用
                    </button>
                  )}
                </div>
              </div>
              
              {/* 错误信息显示 */}
              {authError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-300 text-sm">{authError}</p>
                </div>
              )}

              <div className="grid gap-4">
                {filteredTestCodes.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-white/40 text-lg mb-2">📝</div>
                    <p className="text-white/60">
                      {hideUsedCodes ? '暂无可用测试码' : '暂无测试码'}
                    </p>
                    {hideUsedCodes && stats?.testCodes.some(code => code.status === 'used') && (
                      <button
                        onClick={() => setHideUsedCodes(false)}
                        className="mt-2 text-[#F5B6C1] hover:text-[#F5B6C1]/80 text-sm transition-colors"
                      >
                        显示已使用的测试码
                      </button>
                    )}
                  </div>
                ) : (
                  filteredTestCodes.map((testCode) => (
                    <div
                      key={testCode.code}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                    >
                      <div className="flex items-center gap-4">
                        <code className="text-lg font-mono text-[#F5B6C1] bg-white/10 px-3 py-1 rounded">
                          {testCode.code}
                        </code>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          testCode.status === 'available' ? 'bg-green-500/20 text-green-300' :
                          testCode.status === 'distributed' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }`}>
                          {testCode.status === 'available' ? '可用' :
                           testCode.status === 'distributed' ? '已发放' : '已使用'}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300">
                          {testCode.assessmentType === 'emotional-memory-pattern' ? '情绪记忆' : 
                           testCode.assessmentType === 'big-five-personality' ? '大五人格' : '情绪记忆'}
                        </span>
                        {testCode.usedAt && (
                          <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">
                            {new Date(testCode.usedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyCode(testCode.code)}
                          className="p-2 text-white/60 hover:text-white transition-colors"
                        >
                          {copiedCode === testCode.code ? 
                            <Check className="h-4 w-4 text-green-400" /> :
                            <Copy className="h-4 w-4" />
                          }
                        </button>
                        
                        {testCode.status === 'available' && testCode.source === 'manual' && (
                          <button
                            onClick={() => updateCodeStatus(testCode.code, 'distributed')}
                            className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded text-sm hover:bg-yellow-500/30 transition-colors"
                          >
                            标记已发放
                          </button>
                        )}
                        
                        <span className={`px-2 py-1 rounded text-xs ${
                          testCode.source === 'manual' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                        }`}>
                          {testCode.source === 'manual' ? '手动生成' : '自动生成'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">使用统计</h2>
                <button
                  onClick={loadAdminData}
                  disabled={isLoading}
                  className="px-4 py-2 bg-[#F5B6C1]/20 text-[#F5B6C1] rounded-lg hover:bg-[#F5B6C1]/30 transition-colors disabled:opacity-50"
                >
                  {isLoading ? '刷新中...' : '刷新数据'}
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-6 w-6 text-[#F5B6C1]" />
                    <h3 className="text-lg font-semibold text-white">首页访问次数</h3>
                  </div>
                  <p className="text-3xl font-bold text-[#F5B6C1]">{stats?.totalVisits || 0}</p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart3 className="h-6 w-6 text-[#F5B6C1]" />
                    <h3 className="text-lg font-semibold text-white">进入测评次数</h3>
                  </div>
                  <p className="text-3xl font-bold text-[#F5B6C1]">{stats?.totalTests || 0}</p>
                <div className="mt-2 text-sm text-gray-400">
                  <div>情绪记忆测评: {stats?.testsByType?.['emotional-memory-pattern'] || 0}</div>
  
                  <div>大五人格测评: {stats?.testsByType?.['big-five-personality'] || 0}</div>
                </div>
                </div>
                
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="h-6 w-6 text-[#F5B6C1]" />
                    <h3 className="text-lg font-semibold text-white">测评完成次数</h3>
                  </div>
                  <p className="text-3xl font-bold text-[#F5B6C1]">{stats?.totalCompletions || 0}</p>
                  <div className="mt-2 text-sm text-gray-400">
                    <div>情绪记忆测评: {stats?.completionsByType?.['emotional-memory-pattern'] || 0}</div>
    
                    <div>大五人格测评: {stats?.completionsByType?.['big-five-personality'] || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          )}



          {activeTab === 'team' && <TeamAdmin />}

          {activeTab === 'blog' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">博客管理</h2>
                <button
                  onClick={createNewPost}
                  disabled={isLoading || editingPost !== null}
                  className="flex items-center gap-2 bg-[#F5B6C1] text-[#3A7D78] px-4 py-2 rounded-lg font-medium hover:bg-[#F5B6C1]/90 transition-colors disabled:opacity-50"
                >
                  <Plus className="h-5 w-5" />
                  新建文章
                </button>
              </div>

              {/* 成功/错误消息 */}
              {blogSuccess && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-300 text-sm">{blogSuccess}</p>
                </div>
              )}
              {blogError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-300 text-sm">{blogError}</p>
                </div>
              )}

              {/* 编辑文章表单 */}
              {editingPost && (
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white">
                      {isCreatingPost ? '新建文章' : '编辑文章'}
                    </h3>
                    <button
                      onClick={cancelEdit}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* 标题 */}
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        文章标题 *
                      </label>
                      <input
                        type="text"
                        value={editingPost.title}
                        onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                        placeholder="请输入文章标题"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                      />
                    </div>

                    {/* 摘要 */}
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        文章摘要
                      </label>
                      <textarea
                        value={editingPost.excerpt}
                        onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                        placeholder="请输入文章摘要（留空将自动生成）"
                        rows={3}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent resize-none"
                      />
                    </div>

                    {/* 内容 */}
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        文章内容 *
                      </label>
                      <div className="text-white/60 text-xs mb-2 bg-white/5 p-3 rounded-lg">
                        💡 <strong>格式化提示：</strong>
                        <ul className="mt-1 ml-4 list-disc">
                          <li>直接粘贴文本，系统会自动处理段落格式</li>
                          <li>空行会被转换为段落分隔</li>
                          <li>单个换行会被转换为换行符</li>
                          <li>也支持直接输入HTML格式</li>
                        </ul>
                      </div>
                      <textarea
                        value={editingPost.content}
                        onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                        placeholder="请输入文章内容...\n\n您可以直接粘贴从其他地方复制的文本，系统会自动处理格式。\n\n空行将被视为段落分隔。"
                        rows={12}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent resize-none text-sm leading-relaxed"
                      />
                    </div>

                    {/* 标签 */}
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        标签
                      </label>
                      <input
                        type="text"
                        value={editingPost.tags.join(', ')}
                        onChange={(e) => setEditingPost({ 
                          ...editingPost, 
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                        })}
                        placeholder="请输入标签，用逗号分隔"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                      />
                    </div>

                    {/* 封面图片 */}
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        封面图片URL
                      </label>
                      <input
                        type="url"
                        value={editingPost.coverImage || ''}
                        onChange={(e) => setEditingPost({ ...editingPost, coverImage: e.target.value })}
                        placeholder="请输入封面图片URL"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                      />
                    </div>

                    {/* 状态和作者 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          发布状态
                        </label>
                        <select
                          value={editingPost.status}
                          onChange={(e) => setEditingPost({ ...editingPost, status: e.target.value as 'draft' | 'published' | 'archived' })}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                        >
                          <option value="draft">草稿</option>
                          <option value="published">已发布</option>
                          <option value="archived">已归档</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          作者
                        </label>
                        <input
                          type="text"
                          value={editingPost.author}
                          onChange={(e) => setEditingPost({ ...editingPost, author: e.target.value })}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        onClick={cancelEdit}
                        className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => savePost(editingPost)}
                        disabled={!editingPost.title || !editingPost.content}
                        className="flex items-center gap-2 px-6 py-2 bg-[#F5B6C1] text-[#3A7D78] rounded-lg font-medium hover:bg-[#F5B6C1]/90 transition-colors disabled:opacity-50"
                      >
                        <Save className="h-4 w-4" />
                        {isCreatingPost ? '创建文章' : '保存修改'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 文章列表 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">文章列表</h3>
                {blogPosts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-white/40 text-lg mb-2">📝</div>
                    <p className="text-white/60">还没有任何文章</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blogPosts.map((post) => (
                      <div
                        key={post.id}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-white font-medium">{post.title || '无标题'}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              post.status === 'published' ? 'bg-green-500/20 text-green-300' :
                              post.status === 'draft' ? 'bg-yellow-500/20 text-yellow-300' :
                              'bg-gray-500/20 text-gray-300'
                            }`}>
                              {post.status === 'published' ? '已发布' :
                               post.status === 'draft' ? '草稿' : '已归档'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-white/60 text-sm">
                            <span>作者：{post.author}</span>
                            <span>创建：{new Date(post.publishedAt).toLocaleDateString()}</span>
                            <span>阅读：{post.views} 次</span>
                            {post.tags.length > 0 && (
                              <span>标签：{post.tags.slice(0, 2).join(', ')}{post.tags.length > 2 ? '...' : ''}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingPost(post);
                              setIsCreatingPost(false);
                              setBlogError('');
                              setBlogSuccess('');
                            }}
                            className="p-2 text-white/60 hover:text-[#F5B6C1] transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deletePost(post.id)}
                            className="p-2 text-white/60 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">功能设置</h2>
              
              {/* 测评模式设置 */}
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {testModeOpen[selectedSettingsAssessmentType] ? 
                        <Unlock className="h-6 w-6 text-green-400" /> : 
                        <Lock className="h-6 w-6 text-[#F5B6C1]" />
                      }
                      <h3 className="text-lg font-semibold text-white">测评访问模式</h3>
                    </div>
                    <p className="text-white/60 text-sm">
                      {testModeOpen[selectedSettingsAssessmentType] ? 
                        '开放模式：所有用户都可以直接进行测评，无需验证测试码' : 
                        '验证模式：用户需要输入有效的测试码才能进行测评'
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={selectedSettingsAssessmentType}
                      onChange={(e) => setSelectedSettingsAssessmentType(e.target.value as 'emotional-memory-pattern' | 'big-five-personality')}
                      className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#F5B6C1]"
                    >
                      <option value="emotional-memory-pattern">情绪记忆测评</option>
      
                      <option value="big-five-personality">大五人格测评</option>
                    </select>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={testModeOpen[selectedSettingsAssessmentType]}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          try {
                            const success = await localSettings.setTestModeOpen(newValue, selectedSettingsAssessmentType);
                            if (success) {
                              setTestModeOpen(prev => ({
                                ...prev,
                                [selectedSettingsAssessmentType]: newValue
                              }));
                            } else {
                              console.error('设置开放模式失败');
                              // 可以在这里添加错误提示
                            }
                          } catch (error) {
                            console.error('设置开放模式时发生错误:', error);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>
                
                {/* 模式说明 */}
                <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <Lock className="h-5 w-5 text-[#F5B6C1] mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-white font-medium mb-1">验证模式</h4>
                        <p className="text-white/60 text-sm">适合限量测试、付费测评或需要控制访问量的场景</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Unlock className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-white font-medium mb-1">开放模式</h4>
                        <p className="text-white/60 text-sm">适合公开测试、推广期或希望最大化用户参与的场景</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 推荐功能设置 */}
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">推荐功能</h3>
                    <p className="text-white/60 text-sm">允许用户在完成测评后获取推荐测试码分享给朋友（全局设置）</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stats?.referralEnabled || false}
                      onChange={async (e) => {
                        try {
                          const success = await globalSettings.setReferralEnabled(e.target.checked);
                          if (success) {
                            // 重新获取统计数据以更新UI
                            await loadAdminData();
                          } else {
                            alert('设置推荐功能失败，请稍后重试');
                            // 恢复checkbox状态
                            e.target.checked = !e.target.checked;
                          }
                        } catch (error) {
                          console.error('设置推荐功能失败:', error);
                          alert('设置推荐功能失败，请稍后重试');
                          // 恢复checkbox状态
                          e.target.checked = !e.target.checked;
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F5B6C1]"></div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;